import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, MapPin, Plus, UserPlus } from 'lucide-react';
import { getCustomer } from '../../api/customers';
import { listSites, updateSiteStatus } from '../../api/sites';
import { listUsersForOrg, updateUserStatus } from '../../api/users';
import { useAuth } from '../../auth/useAuth';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { StatusBadge, Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { NewSiteModal } from './NewSiteModal';
import { NewUserModal } from './NewUserModal';
import { EditUserModal } from './EditUserModal';
import { SiteCategoriesModal } from './SiteCategoriesModal';
import { SiteApprovalModal } from './SiteApprovalModal';
import { CustomerSettingsTab } from './CustomerSettingsTab';
import { CustomerCatalogTab } from './CustomerCatalogTab';
import { ReportsTab } from './ReportsTab';
import { isCustomerAdminFor, ROLE_LABELS } from '../../auth/permissions';
import type { AppUser, CustomerSite } from '../../types/api';

type Tab = 'sites' | 'users' | 'catalog' | 'reports' | 'settings';

function userRoleBadges(user: AppUser) {
  const badges: string[] = [];
  if (user.isMspAdmin) badges.push('MSP Admin');
  for (const r of user.orgRoles) badges.push(ROLE_LABELS[r.role]);
  for (const r of user.siteRoles) badges.push(`${ROLE_LABELS[r.role]} (${r.site?.name ?? 'site'})`);
  return [...new Set(badges)];
}

export function CustomerDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const canManage = !!orgId && (currentUser?.isMspAdmin || isCustomerAdminFor(currentUser, orgId));
  const visibleTabs: Tab[] = canManage ? ['sites', 'users', 'catalog', 'reports', 'settings'] : ['reports'];
  const [tab, setTab] = useState<Tab>(canManage ? 'sites' : 'reports');
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [siteStatusTarget, setSiteStatusTarget] = useState<CustomerSite | null>(null);
  const [siteStatusError, setSiteStatusError] = useState<string | null>(null);
  const [categoriesTarget, setCategoriesTarget] = useState<CustomerSite | null>(null);
  const [approvalTarget, setApprovalTarget] = useState<CustomerSite | null>(null);
  const [userStatusTarget, setUserStatusTarget] = useState<AppUser | null>(null);
  const [userStatusError, setUserStatusError] = useState<string | null>(null);
  const [editUserTarget, setEditUserTarget] = useState<AppUser | null>(null);

  const orgQuery = useQuery({ queryKey: ['customer', orgId], queryFn: () => getCustomer(orgId!), enabled: !!orgId });
  const sitesQuery = useQuery({ queryKey: ['sites', orgId], queryFn: () => listSites(orgId!), enabled: !!orgId });
  const usersQuery = useQuery({
    queryKey: ['users', orgId],
    queryFn: () => listUsersForOrg(orgId!),
    enabled: !!orgId,
  });

  const siteStatusMutation = useMutation({
    mutationFn: () =>
      updateSiteStatus(orgId!, siteStatusTarget!.id, siteStatusTarget!.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'),
    onSuccess: (site) => {
      queryClient.invalidateQueries({ queryKey: ['sites', orgId] });
      showToast(`${site.name} ${site.status === 'ACTIVE' ? 'reactivated' : 'deactivated'}.`);
      setSiteStatusTarget(null);
    },
    onError: (err) => setSiteStatusError(apiErrorMessage(err, 'Could not update this site.')),
  });

  const userStatusMutation = useMutation({
    mutationFn: () =>
      updateUserStatus(userStatusTarget!.id, userStatusTarget!.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'),
    onSuccess: (u) => {
      queryClient.invalidateQueries({ queryKey: ['users', orgId] });
      showToast(`${u.email} ${u.status === 'ACTIVE' ? 'reactivated' : 'deactivated'}.`);
      setUserStatusTarget(null);
    },
    onError: (err) => setUserStatusError(apiErrorMessage(err, 'Could not update this user.')),
  });

  if (!orgId) return null;

  return (
    <div>
      <PageHeader
        breadcrumb={
          currentUser?.isMspAdmin ? (
            <div className="flex items-center gap-1 text-xs text-slate-400">
              <Link to="/customers" className="hover:text-slate-600">
                Customers
              </Link>
              <ChevronRight className="h-3 w-3" />
              <span>{orgQuery.data?.name ?? '...'}</span>
            </div>
          ) : undefined
        }
        title={orgQuery.data?.name ?? 'Loading...'}
        actions={
          orgQuery.data && (
            <div className="flex items-center gap-2">
              <StatusBadge status={orgQuery.data.status} kind="org" />
              {orgQuery.data.billingConfig && <Badge tone="blue">{orgQuery.data.billingConfig.defaultChargeType}</Badge>}
            </div>
          )
        }
      />

      {visibleTabs.length > 1 && (
      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {visibleTabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize transition-colors ${
              tab === t ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      )}

      {tab === 'sites' && (
        <Card>
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
            <span className="text-sm font-medium text-slate-600">
              {sitesQuery.data?.length ?? 0} site{sitesQuery.data?.length === 1 ? '' : 's'}
            </span>
            <Button size="sm" onClick={() => setSiteModalOpen(true)}>
              <Plus className="h-3.5 w-3.5" /> New site
            </Button>
          </div>
          {sitesQuery.isLoading ? (
            <div className="py-14 text-center">
              <Spinner />
            </div>
          ) : !sitesQuery.data || sitesQuery.data.length === 0 ? (
            <EmptyState icon={MapPin} title="No sites yet" description="Add a site to start assigning users to it." />
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Address</Th>
                  <Th>Status</Th>
                  <Th>Self-registration</Th>
                  <Th>Approval threshold</Th>
                  <Th></Th>
                </tr>
              </Thead>
              <tbody>
                {sitesQuery.data.map((site) => (
                  <Tr key={site.id}>
                    <Td className="font-medium text-slate-900">{site.name}</Td>
                    <Td>{site.address ?? '-'}</Td>
                    <Td>
                      <StatusBadge status={site.status} kind="org" />
                    </Td>
                    <Td>{site.selfRegistrationEnabled ? <Badge tone="green">Enabled</Badge> : <Badge>Disabled</Badge>}</Td>
                    <Td>
                      {site.approvalThreshold ? (
                        `£${Number(site.approvalThreshold).toFixed(2)}`
                      ) : (
                        <span className="text-slate-400">None</span>
                      )}
                    </Td>
                    <Td className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setCategoriesTarget(site)}>
                          Categories
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setApprovalTarget(site)}>
                          Approval limit
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setSiteStatusError(null);
                            setSiteStatusTarget(site);
                          }}
                        >
                          {site.status === 'ACTIVE' ? 'Deactivate' : 'Reactivate'}
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {tab === 'users' && (
        <Card>
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
            <span className="text-sm font-medium text-slate-600">
              {usersQuery.data?.length ?? 0} user{usersQuery.data?.length === 1 ? '' : 's'}
            </span>
            <Button size="sm" onClick={() => setUserModalOpen(true)}>
              <UserPlus className="h-3.5 w-3.5" /> New user
            </Button>
          </div>
          {usersQuery.isLoading ? (
            <div className="py-14 text-center">
              <Spinner />
            </div>
          ) : !usersQuery.data || usersQuery.data.length === 0 ? (
            <EmptyState icon={UserPlus} title="No users yet" description="Add end users, signing authorities or admins." />
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>Email</Th>
                  <Th>Name</Th>
                  <Th>Roles</Th>
                  <Th>Status</Th>
                  <Th></Th>
                </tr>
              </Thead>
              <tbody>
                {usersQuery.data.map((u) => (
                  <Tr key={u.id}>
                    <Td className="font-medium text-slate-900">{u.email}</Td>
                    <Td>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '-'}</Td>
                    <Td>
                      <div className="flex flex-wrap gap-1">
                        {userRoleBadges(u).map((label) => (
                          <Badge key={label} tone="purple">
                            {label}
                          </Badge>
                        ))}
                      </div>
                    </Td>
                    <Td>
                      <StatusBadge status={u.status} kind="user" />
                    </Td>
                    <Td className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditUserTarget(u)}>
                          Edit
                        </Button>
                        {u.status !== 'LOCKED' && u.id !== currentUser?.id && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setUserStatusError(null);
                              setUserStatusTarget(u);
                            }}
                          >
                            {u.status === 'ACTIVE' ? 'Deactivate' : 'Reactivate'}
                          </Button>
                        )}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {tab === 'catalog' && <CustomerCatalogTab orgId={orgId} sites={sitesQuery.data ?? []} />}

      {tab === 'reports' && (
        <ReportsTab orgId={orgId} canManage={canManage} orgName={orgQuery.data?.name} isMspAdmin={!!currentUser?.isMspAdmin} />
      )}

      {tab === 'settings' && orgQuery.data && <CustomerSettingsTab org={orgQuery.data} sites={sitesQuery.data ?? []} />}

      <NewSiteModal orgId={orgId} open={siteModalOpen} onClose={() => setSiteModalOpen(false)} />
      <NewUserModal
        orgId={orgId}
        sites={sitesQuery.data ?? []}
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
      />
      <EditUserModal
        orgId={orgId}
        sites={sitesQuery.data ?? []}
        open={!!editUserTarget}
        onClose={() => setEditUserTarget(null)}
        user={editUserTarget}
      />
      <SiteCategoriesModal
        orgId={orgId}
        site={categoriesTarget}
        open={!!categoriesTarget}
        onClose={() => setCategoriesTarget(null)}
      />
      <SiteApprovalModal
        orgId={orgId}
        site={approvalTarget}
        open={!!approvalTarget}
        onClose={() => setApprovalTarget(null)}
      />

      <ConfirmModal
        open={!!siteStatusTarget}
        onClose={() => setSiteStatusTarget(null)}
        onConfirm={() => siteStatusMutation.mutate()}
        title={siteStatusTarget?.status === 'ACTIVE' ? 'Deactivate site' : 'Reactivate site'}
        description={
          siteStatusTarget?.status === 'ACTIVE'
            ? `${siteStatusTarget?.name} will no longer be selectable for new orders or self-registration. Its history is kept and this can be reversed at any time.`
            : `${siteStatusTarget?.name} will be available again for orders and self-registration.`
        }
        confirmLabel={siteStatusTarget?.status === 'ACTIVE' ? 'Deactivate' : 'Reactivate'}
        tone={siteStatusTarget?.status === 'ACTIVE' ? 'danger' : 'primary'}
        isLoading={siteStatusMutation.isPending}
        error={siteStatusError}
      />

      <ConfirmModal
        open={!!userStatusTarget}
        onClose={() => setUserStatusTarget(null)}
        onConfirm={() => userStatusMutation.mutate()}
        title={userStatusTarget?.status === 'ACTIVE' ? 'Deactivate user' : 'Reactivate user'}
        description={
          userStatusTarget?.status === 'ACTIVE'
            ? `${userStatusTarget?.email} will no longer be able to sign in. This can be reversed at any time.`
            : `${userStatusTarget?.email} will regain access to sign in.`
        }
        confirmLabel={userStatusTarget?.status === 'ACTIVE' ? 'Deactivate' : 'Reactivate'}
        tone={userStatusTarget?.status === 'ACTIVE' ? 'danger' : 'primary'}
        isLoading={userStatusMutation.isPending}
        error={userStatusError}
      />
    </div>
  );
}
